import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Check, Trash2, Star, Dumbbell, Book, Heart, Music, Code, Coffee, Moon, Sun as SunIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useUser } from '../contexts/UserContext';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const habitIcons = {
  star: Star,
  dumbbell: Dumbbell,
  book: Book,
  heart: Heart,
  music: Music,
  code: Code,
  coffee: Coffee,
  moon: Moon,
  sun: SunIcon
};

const habitColors = [
  '#FF7A59', '#4F8CFF', '#06D6A0', '#FFD166', '#B794F4', '#EF476F', '#118AB2', '#073B4C'
];

export const Habits = () => {
  const { user, getAgeConfig, fetchStats } = useUser();
  const config = getAgeConfig();
  
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newHabit, setNewHabit] = useState({ name: '', icon: 'star', color: '#FF7A59' });

  const today = new Date().toISOString().split('T')[0];

  const fetchHabits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/habits`, { withCredentials: true });
      setHabits(response.data);
    } catch (error) {
      console.error('Error fetching habits:', error);
      toast.error('Error al cargar hábitos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  const createHabit = async () => {
    if (!newHabit.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      const response = await axios.post(`${API}/habits`, newHabit, { withCredentials: true });
      setHabits([...habits, response.data]);
      setNewHabit({ name: '', icon: 'star', color: '#FF7A59' });
      setIsDialogOpen(false);
      toast.success('¡Hábito creado!');
    } catch (error) {
      console.error('Error creating habit:', error);
      toast.error('Error al crear hábito');
    }
  };

  const toggleHabit = async (habitId) => {
    try {
      const response = await axios.post(`${API}/habits/${habitId}/complete`, {}, { withCredentials: true });
      setHabits(habits.map(h => h.habit_id === habitId ? response.data.habit : h));
      fetchStats();
      
      const isCompleted = response.data.habit.completed_dates?.includes(today);
      if (isCompleted) {
        toast.success('¡+5 de energía para MIMO!');
      }
    } catch (error) {
      console.error('Error toggling habit:', error);
      toast.error('Error al actualizar hábito');
    }
  };

  const deleteHabit = async (habitId) => {
    try {
      await axios.delete(`${API}/habits/${habitId}`, { withCredentials: true });
      setHabits(habits.filter(h => h.habit_id !== habitId));
      fetchStats();
      toast.success('Hábito eliminado');
    } catch (error) {
      console.error('Error deleting habit:', error);
      toast.error('Error al eliminar hábito');
    }
  };

  const completedToday = habits.filter(h => h.completed_dates?.includes(today)).length;

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8 lg:pl-20">
      <Navigation />
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className={`${config.fontClass} text-2xl sm:text-3xl font-bold text-foreground`}>
              Mis Hábitos
            </h1>
            <p className="text-muted-foreground">
              {completedToday} de {habits.length} completados hoy
            </p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#FF7A59] hover:bg-[#E66A4B] text-white rounded-full"
                data-testid="add-habit-btn"
              >
                <Plus className="w-5 h-5 mr-1" />
                Nuevo
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Hábito</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <Input
                  placeholder="Nombre del hábito"
                  value={newHabit.name}
                  onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
                  data-testid="habit-name-input"
                />
                
                {/* Icon selector */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Icono</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(habitIcons).map(([key, Icon]) => (
                      <button
                        key={key}
                        onClick={() => setNewHabit({ ...newHabit, icon: key })}
                        className={`p-2 rounded-lg transition-colors ${
                          newHabit.icon === key 
                            ? 'bg-[#FF7A59]/20 ring-2 ring-[#FF7A59]' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                        data-testid={`icon-${key}`}
                      >
                        <Icon className="w-5 h-5" />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Color selector */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {habitColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewHabit({ ...newHabit, color })}
                        className={`w-8 h-8 rounded-full transition-transform ${
                          newHabit.color === color ? 'ring-2 ring-offset-2 ring-foreground scale-110' : ''
                        }`}
                        style={{ backgroundColor: color }}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={createHabit}
                  className="w-full bg-[#FF7A59] hover:bg-[#E66A4B] text-white"
                  data-testid="create-habit-btn"
                >
                  Crear Hábito
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Progress bar */}
        {habits.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#06D6A0] rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(completedToday / habits.length) * 100}%` }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                />
              </div>
              <span className="text-sm font-medium text-foreground">
                {Math.round((completedToday / habits.length) * 100)}%
              </span>
            </div>
          </motion.div>
        )}

        {/* Habits list */}
        <div className="space-y-3">
          <AnimatePresence>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
                ))}
              </div>
            ) : habits.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <p className="text-muted-foreground mb-4">
                  Aún no tienes hábitos. ¡Crea tu primero!
                </p>
                <Button
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-[#FF7A59] hover:bg-[#E66A4B] text-white rounded-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Crear mi primer hábito
                </Button>
              </motion.div>
            ) : (
              habits.map((habit, index) => {
                const Icon = habitIcons[habit.icon] || Star;
                const isCompleted = habit.completed_dates?.includes(today);
                
                return (
                  <motion.div
                    key={habit.habit_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    layout
                  >
                    <Card className={`glass ${config.cardRadius} overflow-hidden`}>
                      <CardContent className="p-0">
                        <div className="flex items-center">
                          {/* Completion button */}
                          <button
                            onClick={() => toggleHabit(habit.habit_id)}
                            className="p-4 flex-shrink-0"
                            data-testid={`habit-toggle-${habit.habit_id}`}
                          >
                            <motion.div
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors`}
                              style={{ 
                                backgroundColor: isCompleted ? habit.color : `${habit.color}20`,
                                border: isCompleted ? 'none' : `2px solid ${habit.color}`
                              }}
                              whileTap={{ scale: 0.9 }}
                            >
                              {isCompleted && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: "spring", stiffness: 500 }}
                                >
                                  <Check className="w-5 h-5 text-white" />
                                </motion.div>
                              )}
                            </motion.div>
                          </button>
                          
                          {/* Habit info */}
                          <div className="flex-1 py-4">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" style={{ color: habit.color }} />
                              <span className={`font-medium ${
                                isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'
                              }`}>
                                {habit.name}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {habit.completed_dates?.length || 0} veces completado
                            </p>
                          </div>
                          
                          {/* Delete button */}
                          <button
                            onClick={() => deleteHabit(habit.habit_id)}
                            className="p-4 text-muted-foreground hover:text-destructive transition-colors"
                            data-testid={`habit-delete-${habit.habit_id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default Habits;
